import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import type {
  Process,
  ScheduledFunction,
  ScheduledFunctionHttpMethod,
  ScheduleType,
  ScheduledFunctionHeader,
  CreateScheduledFunctionRequest,
  UpdateScheduledFunctionRequest,
} from '@techgitter/contracts';

// sm4rt-api runs `internal://` rows in-process (scheduled_internal_targets.py):
// its own Easy-Auth'd jobs, which the dispatcher can't reach over HTTP. Which
// targets exist is the API's allow-list; an unknown one is recorded as an
// error on the row's last run.
export function isValidTargetUrl(url: string): boolean {
  return /^https?:\/\/.+/.test(url) || /^internal:\/\/.+/i.test(url);
}

interface ScheduledFunctionDialogProps {
  open: boolean;
  scheduledFunction: ScheduledFunction | null;
  processes: Process[];
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (payload: CreateScheduledFunctionRequest | UpdateScheduledFunctionRequest) => Promise<void>;
}

export const ScheduledFunctionDialog: React.FC<ScheduledFunctionDialogProps> = ({
  open,
  scheduledFunction,
  processes,
  isSaving,
  error,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [httpMethod, setHttpMethod] = useState<ScheduledFunctionHttpMethod>('POST');
  const [scheduleType, setScheduleType] = useState<ScheduleType>('daily');
  const [scheduledTime, setScheduledTime] = useState('08:00');
  const [isEnabled, setIsEnabled] = useState(true);
  const [timeoutSeconds, setTimeoutSeconds] = useState<string>('');
  const [headers, setHeaders] = useState<ScheduledFunctionHeader[]>([]);
  const [processId, setProcessId] = useState<string>('');

  useEffect(() => {
    if (scheduledFunction) {
      setName(scheduledFunction.name ?? '');
      setDescription(scheduledFunction.description ?? '');
      setUrl(scheduledFunction.url ?? '');
      setHttpMethod(scheduledFunction.httpMethod);
      setScheduleType(scheduledFunction.scheduleType);
      setScheduledTime(scheduledFunction.scheduledTime);
      setIsEnabled(scheduledFunction.isEnabled);
      setTimeoutSeconds(scheduledFunction.timeoutSeconds != null ? String(scheduledFunction.timeoutSeconds) : '');
      setHeaders(scheduledFunction.headers ?? []);
      setProcessId(scheduledFunction.processId ?? '');
    } else {
      setName('');
      setDescription('');
      setUrl('');
      setHttpMethod('POST');
      setScheduleType('daily');
      setScheduledTime('08:00');
      setIsEnabled(true);
      setTimeoutSeconds('');
      setHeaders([]);
      setProcessId('');
    }
  }, [scheduledFunction]);

  const isCreate = scheduledFunction === null;

  const trimmedName = name.trim();
  const trimmedUrl = url.trim();

  const trimmedTimeout = timeoutSeconds.trim();
  const parsedTimeout = trimmedTimeout === '' ? undefined : Number(trimmedTimeout);
  const isTimeoutValid =
    parsedTimeout === undefined ||
    (Number.isInteger(parsedTimeout) && parsedTimeout >= 1 && parsedTimeout <= 600);

  const isValid =
    trimmedName.length > 0 &&
    trimmedUrl.length > 0 &&
    isValidTargetUrl(trimmedUrl) &&
    /^\d{2}:\d{2}$/.test(scheduledTime) &&
    isTimeoutValid;

  const isDirty = isCreate || (
    trimmedName !== (scheduledFunction.name ?? '') ||
    description.trim() !== (scheduledFunction.description ?? '') ||
    trimmedUrl !== (scheduledFunction.url ?? '') ||
    httpMethod !== scheduledFunction.httpMethod ||
    scheduleType !== scheduledFunction.scheduleType ||
    scheduledTime !== scheduledFunction.scheduledTime ||
    isEnabled !== scheduledFunction.isEnabled ||
    parsedTimeout !== scheduledFunction.timeoutSeconds ||
    processId !== (scheduledFunction.processId ?? '') ||
    JSON.stringify(headers) !== JSON.stringify(scheduledFunction.headers ?? [])
  );

  const handleSave = async () => {
    if (!isDirty || !isValid) return;

    const payload: CreateScheduledFunctionRequest = {
      name: trimmedName,
      description: description.trim(),
      url: trimmedUrl,
      httpMethod,
      scheduleType,
      scheduledTime,
      isEnabled,
      timeoutSeconds: parsedTimeout,
      headers: headers.length > 0 ? headers : undefined,
      // Always send processId on edit so an empty selection clears the link;
      // on create only send when set.
      ...(isCreate ? (processId ? { processId } : {}) : { processId }),
    };

    await onSave(payload);
  };

  const handleAddHeader = () => {
    setHeaders([...headers, { key: '', value: '' }]);
  };

  const handleRemoveHeader = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const handleHeaderChange = (index: number, field: 'key' | 'value', value: string) => {
    setHeaders(headers.map((h, i) => (i === index ? { ...h, [field]: value } : h)));
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isCreate ? 'Add Scheduled Function' : 'Edit Scheduled Function'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label="URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            fullWidth
            placeholder="https://example.com/api/function"
            error={trimmedUrl.length > 0 && !isValidTargetUrl(trimmedUrl)}
            helperText="An http(s):// URL, or internal://… for a job sm4rt-api runs itself"
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>HTTP Method</Typography>
            <ToggleButtonGroup
              value={httpMethod}
              exclusive
              onChange={(_, val) => { if (val) setHttpMethod(val); }}
              size="small"
            >
              <ToggleButton value="GET">GET</ToggleButton>
              <ToggleButton value="POST">POST</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>Schedule</Typography>
            <ToggleButtonGroup
              value={scheduleType}
              exclusive
              onChange={(_, val) => { if (val) setScheduleType(val); }}
              size="small"
            >
              <ToggleButton value="daily">Daily</ToggleButton>
              <ToggleButton value="weekday">Weekdays</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <TextField
            label="Time"
            type="time"
            value={scheduledTime}
            onChange={(e) => setScheduledTime(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: 150 }}
          />
          <TextField
            label="Timeout (seconds)"
            type="number"
            value={timeoutSeconds}
            onChange={(e) => setTimeoutSeconds(e.target.value)}
            placeholder="120"
            helperText={isTimeoutValid ? 'Defaults to 120s if blank' : 'Must be between 1 and 600'}
            error={!isTimeoutValid}
            slotProps={{ htmlInput: { min: 1, max: 600, step: 1 } }}
            sx={{ width: 200 }}
          />
          <TextField
            select
            label="Process"
            value={processId}
            onChange={(e) => setProcessId(e.target.value)}
            helperText="Optional — link this function to a process"
            fullWidth
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {processes.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
              />
            }
            label="Enabled"
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>Headers</Typography>
            {headers.map((header, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <TextField
                  label="Key"
                  value={header.key}
                  onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Value"
                  value={header.value}
                  onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <IconButton size="small" onClick={() => handleRemoveHeader(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={handleAddHeader}>
              Add Header
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isDirty || !isValid || isSaving}
        >
          {isSaving ? 'Saving...' : isCreate ? 'Create' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
